import React, { useId } from 'react'
import './legalDocument.css'

export interface LegalDocumentProps {
  rawMarkdown: string
  title?: string
  className?: string
  testId?: string
}

interface RawListItem {
  indent: number
  isOrdered: boolean
  text: string
}

interface NestedListItem {
  isOrdered: boolean
  text: string
  children: NestedListItem[]
}

const parseInline = (text: string): React.ReactNode[] => {
  // Matches **bold** or `code`
  const regex = /(\*\*[^*]+\*\*|`[^`]+`)/g
  const parts = text.split(regex)

  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**') && part.length >= 4) {
      return <strong key={index}>{part.slice(2, -2)}</strong>
    }
    if (part.startsWith('`') && part.endsWith('`') && part.length >= 2) {
      return <code key={index}>{part.slice(1, -1)}</code>
    }
    return part
  })
}

const buildNestedList = (items: RawListItem[]): NestedListItem[] => {
  const result: NestedListItem[] = []
  const stack: { indent: number; node: NestedListItem }[] = []

  for (const item of items) {
    const node: NestedListItem = {
      isOrdered: item.isOrdered,
      text: item.text,
      children: [],
    }

    while (stack.length > 0 && stack[stack.length - 1].indent >= item.indent) {
      stack.pop()
    }

    if (stack.length === 0) {
      result.push(node)
    } else {
      stack[stack.length - 1].node.children.push(node)
    }

    stack.push({ indent: item.indent, node })
  }

  return result
}

const renderNestedList = (items: NestedListItem[], keyPrefix = 'list'): React.ReactElement => {
  if (items.length === 0) return <React.Fragment />
  const isOrdered = items[0].isOrdered

  const listItems = items.map((item, index) => (
    <li key={`${keyPrefix}-${index}`}>
      {parseInline(item.text)}
      {item.children.length > 0 && renderNestedList(item.children, `${keyPrefix}-${index}-sub`)}
    </li>
  ))

  return isOrdered ? (
    <ol className="legalList legalList--ordered">{listItems}</ol>
  ) : (
    <ul className="legalList legalList--unordered">{listItems}</ul>
  )
}

export const LegalDocument: React.FC<LegalDocumentProps> = ({
  rawMarkdown,
  title,
  className = '',
  testId = 'legal-document',
}) => {
  const uniquePrefix = useId()
  const slugCounts = new Map<string, number>()

  const getSlugId = (headingText: string): string => {
    const baseSlug = headingText
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '') || 'section'
    const currentCount = slugCounts.get(baseSlug) ?? 0
    slugCounts.set(baseSlug, currentCount + 1)
    return currentCount === 0 ? baseSlug : `${baseSlug}-${currentCount}`
  }

  const rawLines = rawMarkdown.split('\n')
  let draftBanner: string | null = null
  let startIndex = 0

  if (rawLines[0]?.trim().startsWith('DRAFT —')) {
    draftBanner = rawLines[0].trim()
    startIndex = 1
  }

  const elements: React.ReactNode[] = []
  let lineIdx = startIndex

  while (lineIdx < rawLines.length) {
    const rawLine = rawLines[lineIdx]
    const trimmed = rawLine.trim()

    if (!trimmed) {
      lineIdx++
      continue
    }

    // Headings
    const headingMatch = trimmed.match(/^(#{1,3})\s+(.*)$/)
    if (headingMatch) {
      const level = headingMatch[1].length
      const headingContent = headingMatch[2]
      const slug = getSlugId(headingContent)

      if (level === 1) {
        elements.push(
          <h1 key={`${uniquePrefix}-h1-${lineIdx}`} id={slug} className="legalH1">
            {parseInline(headingContent)}
          </h1>,
        )
      } else if (level === 2) {
        elements.push(
          <h2 key={`${uniquePrefix}-h2-${lineIdx}`} id={slug} className="legalH2">
            {parseInline(headingContent)}
          </h2>,
        )
      } else {
        elements.push(
          <h3 key={`${uniquePrefix}-h3-${lineIdx}`} id={slug} className="legalH3">
            {parseInline(headingContent)}
          </h3>,
        )
      }
      lineIdx++
      continue
    }

    // Horizontal rule
    if (trimmed === '---') {
      elements.push(<hr key={`${uniquePrefix}-hr-${lineIdx}`} className="legalHr" />)
      lineIdx++
      continue
    }

    // Table
    if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
      const tableLines: string[] = []
      while (
        lineIdx < rawLines.length &&
        rawLines[lineIdx].trim().startsWith('|') &&
        rawLines[lineIdx].trim().endsWith('|')
      ) {
        tableLines.push(rawLines[lineIdx].trim())
        lineIdx++
      }

      if (tableLines.length >= 2) {
        const parseRow = (line: string): string[] => {
          const cells = line.split('|').map((c) => c.trim())
          return cells.slice(1, -1)
        }

        const headerCells = parseRow(tableLines[0])
        // tableLines[1] is the separator (| :--- |)
        const bodyRows = tableLines.slice(2).map(parseRow)

        elements.push(
          <div key={`${uniquePrefix}-table-${lineIdx}`} className="legalTableWrapper">
            <table className="legalTable">
              <thead>
                <tr>
                  {headerCells.map((header, hIdx) => (
                    <th key={`th-${hIdx}`} scope="col" className="legalTh">
                      {parseInline(header)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {bodyRows.map((rowCells, rIdx) => (
                  <tr key={`tr-${rIdx}`}>
                    {rowCells.map((cell, cIdx) => (
                      <td key={`td-${rIdx}-${cIdx}`} className="legalTd">
                        {parseInline(cell)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>,
        )
      }
      continue
    }

    // List items
    const listMatch = rawLine.match(/^(\s*)([-*]|\d+\.)\s+(.*)$/)
    if (listMatch) {
      const listItems: RawListItem[] = []
      while (lineIdx < rawLines.length) {
        const currentRaw = rawLines[lineIdx]
        const currentMatch = currentRaw.match(/^(\s*)([-*]|\d+\.)\s+(.*)$/)
        if (!currentMatch) {
          break
        }
        listItems.push({
          indent: currentMatch[1].length,
          isOrdered: /^\d+\./.test(currentMatch[2]),
          text: currentMatch[3],
        })
        lineIdx++
      }

      const nested = buildNestedList(listItems)
      elements.push(
        <div key={`${uniquePrefix}-list-${lineIdx}`} className="legalListBlock">
          {renderNestedList(nested, `${uniquePrefix}-list-${lineIdx}`)}
        </div>,
      )
      continue
    }

    // Paragraph
    const paragraphLines: string[] = []
    while (
      lineIdx < rawLines.length &&
      rawLines[lineIdx].trim() &&
      !rawLines[lineIdx].trim().startsWith('#') &&
      rawLines[lineIdx].trim() !== '---' &&
      !(rawLines[lineIdx].trim().startsWith('|') && rawLines[lineIdx].trim().endsWith('|')) &&
      !/^(\s*)([-*]|\d+\.)\s+/.test(rawLines[lineIdx])
    ) {
      paragraphLines.push(rawLines[lineIdx].trim())
      lineIdx++
    }

    if (paragraphLines.length > 0) {
      const paragraphText = paragraphLines.join(' ')
      elements.push(
        <p key={`${uniquePrefix}-p-${lineIdx}`} className="legalParagraph">
          {parseInline(paragraphText)}
        </p>,
      )
    }
  }

  return (
    <article className={`legalDocument ${className}`.trim()} data-testid={testId} aria-label={title}>
      {draftBanner && (
        <aside className="legalDraftBanner" role="note">
          <p>{draftBanner}</p>
        </aside>
      )}
      <div className="legalBody">{elements}</div>
    </article>
  )
}
